import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import SideBar from "./SideBar";
import UserProfileMenu from "./UserProfileMenu";
import { authFetch } from "../utils/auth";
import { useTenantConfig } from "../tenant/tenantConfig";
import { productTone } from "../tenant/productTone";

const FONT = "'Space Grotesk', sans-serif";

const truncate = (text, n = 160) => {
  const t = (text || "").replace(/\s+/g, " ").trim();
  return t.length <= n ? t : t.slice(0, n - 1).trimEnd() + "…";
};

// Colour by the product's position in the tenant's catalog (productTone), never by name.
const ProductBadge = ({ product, products }) => {
  const tone = productTone(product, products);
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold tracking-wide ${tone.badge}`}
    >
      {product || "—"}
    </span>
  );
};

const OpenCasesAnalysis = ({ onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { products } = useTenantConfig();

  const [cases, setCases] = useState([]);
  const [counts, setCounts] = useState({ count: 0, byProduct: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchMsg, setSearchMsg] = useState("");
  const [searchResults, setSearchResults] = useState(null); // null = show full list

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await authFetch("/api/open-cases");
        if (!res.ok) {
          const e = await res.json().catch(() => ({}));
          throw new Error(e.detail || `Server error ${res.status}`);
        }
        const data = await res.json();
        if (!active) return;
        setCases(Array.isArray(data.cases) ? data.cases : []);
        const byProduct =
          data.counts_by_product && typeof data.counts_by_product === "object" ? data.counts_by_product : {};
        setCounts({ count: data.count || 0, byProduct });
      } catch (err) {
        if (active) setError(err.message || "Failed to load open cases.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const openCase = (caseId) => {
    if (!caseId) return;
    window.open(`${window.location.origin}/case-analysis/${encodeURIComponent(caseId)}`, "_blank", "noopener");
  };

  // Search hits Qdrant (authenticated) by case # or case id and shows the match
  // in the table below — does NOT redirect. Open button opens the case.
  const runSearch = async (e) => {
    if (e) e.preventDefault();
    const q = search.trim();
    if (!q || searching) return;
    setSearching(true);
    setSearchMsg("");
    try {
      const res = await authFetch(`/api/open-cases/search?q=${encodeURIComponent(q)}`);
      if (res.status === 404) {
        setSearchResults([]);
        setSearchMsg(`No case found for "${q}".`);
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Server error ${res.status}`);
      }
      const data = await res.json();
      const c = data.case;
      if (!c?.caseId) {
        setSearchResults([]);
        setSearchMsg(`No case found for "${q}".`);
        return;
      }
      setSearchResults([c]);
    } catch (err) {
      setSearchMsg(err.message || "Search failed.");
    } finally {
      setSearching(false);
    }
  };

  const clearSearch = () => {
    setSearch("");
    setSearchMsg("");
    setSearchResults(null);
  };

  const sortedCases = useMemo(
    () => [...cases].sort((a, b) => (a.productType || "").localeCompare(b.productType || "")),
    [cases]
  );

  // One pill per product the cases carry: catalog order first, then any the catalog lacks.
  const productCounts = useMemo(() => {
    const entries = Object.entries(counts.byProduct || {});
    const rank = (name) => {
      const i = products.findIndex((p) => p.toLowerCase() === name.toLowerCase());
      return i >= 0 ? i : products.length;
    };
    return entries.sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b));
  }, [counts.byProduct, products]);

  // Table shows Qdrant search results when a search is active, else the full list.
  const displayCases = searchResults !== null ? searchResults : sortedCases;
  const isSearchView = searchResults !== null;

  return (
    <div className="flex w-full min-h-screen h-screen bg-[#FAFAF6] overflow-hidden" style={{ fontFamily: FONT }}>
      <SideBar
        mode="agent"
        onLogout={onLogout}
        isDarkMode={false}
        onToggleTheme={() => {}}
        onLinkClick={(p) => navigate(p)}
        currentPath={location.pathname}
      />

      <div className="flex-1 flex flex-col h-full relative overflow-hidden">
        <div className="absolute top-4 right-4 z-50">
          <UserProfileMenu onLogout={onLogout} />
        </div>

        {/* Header */}
        <div className="px-8 pt-7 pb-4 border-b border-[#06b6d4]/30 bg-[#FAFAF6] flex-shrink-0">
          <h1 className="text-3xl font-bold text-[#253439]">Open Cases Analysis</h1>
          <p className="text-sm text-gray-500 mt-1">
            Jump into an in-flight case and get next steps, possible causes, and clarifying questions —
            mid-discussion, without starting over.
          </p>
          <div className="flex items-center gap-2 mt-3 text-xs">
            <span className="px-2.5 py-1 rounded-full bg-white border border-gray-200 text-gray-600 font-medium">
              {counts.count} open cases
            </span>
            {productCounts.map(([product, n]) => (
              <span
                key={product}
                className={`px-2.5 py-1 rounded-full border font-medium ${productTone(product, products).pill}`}
              >
                {n} {product}
              </span>
            ))}
          </div>

          {/* Search Qdrant by case # / case id (opens the matched case) */}
          <form onSubmit={runSearch} className="mt-3 flex items-center gap-2">
            <div className="relative flex-1 max-w-sm">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                width="15"
                height="15"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <circle cx="11" cy="11" r="7" />
                <path strokeLinecap="round" d="M21 21l-4.3-4.3" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  if (searchMsg) setSearchMsg("");
                  if (searchResults !== null && e.target.value.trim() === "") setSearchResults(null);
                }}
                placeholder="Search case # or case ID…"
                className="w-full pl-9 pr-8 py-2 text-sm rounded-lg border border-gray-200 bg-white text-[#253439] focus:outline-none focus:ring-2 focus:ring-[#06b6d4]/40 focus:border-[#06b6d4]"
              />
              {search && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              )}
            </div>
            <button
              type="submit"
              disabled={!search.trim() || searching}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-[#06b6d4] text-white hover:bg-[#0891b2] disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
            >
              {searching ? "Searching…" : "Search"}
            </button>
          </form>
          {searchMsg && <p className="mt-2 text-xs text-red-600">{searchMsg}</p>}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6">
          {loading ? (
            <div className="flex items-center gap-2 text-gray-400 text-sm mt-10 justify-center">
              <div className="flex items-center gap-1">
                {[0, 150, 300].map((d) => (
                  <span key={d} className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />
                ))}
              </div>
              Loading open cases…
            </div>
          ) : error ? (
            <div className="max-w-xl mx-auto mt-10 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          ) : displayCases.length === 0 ? (
            <div className="text-center text-gray-400 text-sm mt-10">
              {isSearchView ? `No case found for "${search.trim()}".` : "No open cases available."}
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
              {isSearchView && (
                <div className="flex items-center justify-between px-4 py-2 bg-cyan-50 border-b border-cyan-200 text-xs text-cyan-800">
                  <span>
                    Search result{displayCases.length > 1 ? "s" : ""} for{" "}
                    <span className="font-semibold">"{search.trim()}"</span>
                  </span>
                  <button onClick={clearSearch} className="font-medium hover:underline">
                    ← Back to all open cases
                  </button>
                </div>
              )}
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200 text-[11px] uppercase tracking-wide text-gray-500">
                    <th className="px-4 py-3 font-semibold">Product</th>
                    <th className="px-4 py-3 font-semibold">Case #</th>
                    <th className="px-4 py-3 font-semibold">Summary</th>
                    <th className="px-4 py-3 font-semibold text-right">Analyze</th>
                  </tr>
                </thead>
                <tbody>
                  {displayCases.map((c) => (
                    <tr
                      key={c.caseId}
                      onClick={() => openCase(c.caseId)}
                      className="border-b border-gray-100 last:border-0 hover:bg-cyan-50/50 cursor-pointer transition-colors group"
                    >
                      <td className="px-4 py-3 align-top">
                        <ProductBadge product={c.productType} products={products} />
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span className="text-sm font-semibold text-[#06b6d4] font-mono whitespace-nowrap">
                          {c.caseNumber || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top max-w-[420px]">
                        <span className="text-sm text-[#253439]">{truncate(c.reported_symptoms || c.caseDescription, 170)}</span>
                      </td>
                      <td className="px-4 py-3 align-top text-right">
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-400 group-hover:text-[#06b6d4] transition-colors whitespace-nowrap">
                          Open
                          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                          </svg>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && !error && displayCases.length > 0 && (
            <p className="text-xs text-gray-400 mt-4">
              Click any case to open <span className="font-medium">Case Analysis and Next Steps</span> in a new tab.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default OpenCasesAnalysis;
